import { isoToJalali, jalaliWeekday } from '~/utils/jalali'

export type HolidaySource = 'weekend' | 'solar' | 'lunar'

export interface HolidayInfo {
  title: string
  source: HolidaySource
  /** Lunar dates follow sighting of the moon, so they can shift by a day. */
  approximate: boolean
}

/** Official public holidays fixed to the solar (Jalali) calendar: `MM-DD`. */
const SOLAR_HOLIDAYS: Record<string, string> = {
  '01-01': 'نوروز',
  '01-02': 'نوروز',
  '01-03': 'نوروز',
  '01-04': 'نوروز',
  '01-12': 'روز جمهوری اسلامی',
  '01-13': 'روز طبیعت',
  '03-14': 'رحلت امام خمینی',
  '03-15': 'قیام ۱۵ خرداد',
  '11-22': 'پیروزی انقلاب اسلامی',
  '12-29': 'ملی شدن صنعت نفت',
}

/** Official public holidays fixed to the lunar (Hijri) calendar: `MM-DD`. */
const LUNAR_HOLIDAYS: Record<string, string> = {
  '01-09': 'تاسوعای حسینی',
  '01-10': 'عاشورای حسینی',
  '02-20': 'اربعین حسینی',
  '02-28': 'رحلت پیامبر و شهادت امام حسن',
  '02-30': 'شهادت امام رضا',
  '03-08': 'شهادت امام حسن عسکری',
  '03-17': 'میلاد پیامبر و امام صادق',
  '06-03': 'شهادت حضرت فاطمه',
  '07-13': 'ولادت امام علی',
  '07-27': 'مبعث پیامبر',
  '08-15': 'ولادت امام زمان',
  '09-21': 'شهادت امام علی',
  '10-01': 'عید فطر',
  '10-02': 'تعطیل عید فطر',
  '10-25': 'شهادت امام صادق',
  '12-10': 'عید قربان',
  '12-18': 'عید غدیر خم',
}

let hijriFormatter: Intl.DateTimeFormat | null = null

function hijriParts(iso: string) {
  if (!hijriFormatter) {
    hijriFormatter = new Intl.DateTimeFormat('en-u-ca-islamic-umalqura-nu-latn', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      timeZone: 'UTC',
    })
  }
  const [gy, gm, gd] = iso.split('-').map(Number) as [number, number, number]
  const parts = hijriFormatter.formatToParts(new Date(Date.UTC(gy, gm - 1, gd)))
  const read = (type: string) => Number(parts.find((part) => part.type === type)?.value)
  return { month: read('month'), day: read('day') }
}

function pad(value: number) {
  return String(value).padStart(2, '0')
}

/**
 * Official Iranian holiday for a Gregorian `YYYY-MM-DD`, or `null` for a
 * working day. Solar holidays and Fridays are exact; lunar ones are computed
 * from the Umm al-Qura calendar and may differ by a day from the announced date.
 */
export function iranHoliday(iso: string): HolidayInfo | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null
  const { jy, jm, jd } = isoToJalali(iso)

  const solar = SOLAR_HOLIDAYS[`${pad(jm)}-${pad(jd)}`]
  if (solar) return { title: solar, source: 'solar', approximate: false }

  try {
    const { month, day } = hijriParts(iso)
    const lunar = LUNAR_HOLIDAYS[`${pad(month)}-${pad(day)}`]
    if (lunar) return { title: lunar, source: 'lunar', approximate: true }
  }
  catch {
    // Runtime without the Islamic calendar in ICU — fall through to weekend.
  }

  if (jalaliWeekday(jy, jm, jd) === 6) {
    return { title: 'جمعه', source: 'weekend', approximate: false }
  }
  return null
}
